import { HttpErrorResponse } from "@angular/common/http";
import { FormBuilder, FormGroup, FormControl, Validators } from "@angular/forms";
import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, BehaviorSubject, from, Observable } from "rxjs";
import { tap, mergeMap } from "rxjs/operators";

import * as jp from 'jsonpath';

import { DocumentManagementService, IUploadFile } from "../../services/document-management.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { LoaderService } from "../../services/loader.service";
import { WorkOrderService } from "../../services/work-order.service";
import { FlowHistory, EntryType } from "../../models/flow.dto";
import { FileUploadQuestion } from "../../../flow/models/flow.model";
import { FlowHistoryService } from "../../../flow/services/flow-history.service";
import { ScanOrder, WorkOrderStatus } from "../../models/work-order.model";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { OnBaseIntegrationAndMappingService } from "../../services/onbase-integration-mapping.service";
import { OnBaseDocumentType } from '../../models/onBaseIntegrationAndMapping.model';


@Component({
    selector: "app-upload-scanned-documents",
    templateUrl: "./upload-scanned-documents.component.html",
    styleUrls: ["./upload-scanned-documents.component.scss"],
    standalone: false
})
export class UploadScannedDocumentsComponent implements OnInit, OnDestroy {

    _defaultDocType: OnBaseDocumentType = { id: -1, name: "--Select--" };


    get applicationNumber(): string {
        return (this.workOrder) ? this.workOrder.applicationNumber : "";
    }
    get applicationType(): string {
        return (this.workOrder) ? this.workOrder.description : "";
    }
    get companyName(): string {
        return (this.workOrder) ? this.workOrder.companyName : "";
    }
    completeText: string = "Have all expected scans been received from the submitter, scanned and uploaded?  Clicking \"Confirm\" will complete the scanning task, and you will be unable to upload any further documents to application";
    completeDialog: Dialog = new Dialog(this.completeText, [new DialogButton("Cancel", false, () => this.onDialogClose()), new DialogButton("Confirm", true, () => this.onDialogConfirm)]);
    documentTypes: OnBaseDocumentType[];

    @ViewChild("downloadLink", { static: true }) download: ElementRef;
    errorDialog: Dialog = new Dialog("An error has occurred in uploading the document.  Please try again.", [new DialogButton("OK", false, () => this.onDialogClose())]);
    filesForm: FormGroup = new FormGroup({});
    genericErrorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", false, () => this.onDialogClose())]);

    get paperQuestion(): FileUploadQuestion | undefined {
        return this.workOrder.uploads.find(up => up.contextTarget === "$.paperApplication");
    }

    resolveResponse: ScanOrder | HttpErrorResponse;
    subheader: SubHeader = new SubHeader({ title: "Scan Documents", buttons: [] });
    subscription: Subscription = new Subscription();
    uploadForm: FormGroup;

    

    workOrder: ScanOrder;
    workOrderLoading$ = new BehaviorSubject<boolean>(true);

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private builder: FormBuilder,
        private readonly loader: LoaderService,
        private readonly dialogService: DynamicDialogService,
        private readonly workOrderService: WorkOrderService,
        private readonly historyService: FlowHistoryService,
        private readonly documentService: DocumentManagementService,
        private readonly mappingService: OnBaseIntegrationAndMappingService,
        readonly subheaderService: SubHeaderService
    ) { }

    ngOnInit() {
        let routeSubscription = this.route.data.pipe(
            tap((data) => {
                this.loader.toggleLoader(false);
                this.resolveResponse = data.workflow;
                if (this.resolveResponse instanceof HttpErrorResponse) {
                    console.log(`Error occurred in resolver - ${this.resolveResponse}`);
                } else {
                    let order = this.resolveResponse as ScanOrder;
                    let items = {};
                    let fileItems = {};
                    for (let i = 0; i < order.uploads.length; i++) {
                        let question = order.uploads[i];
                        items[question.key] = question.getFormControl(false);
                        fileItems[question.key] = question.getFormControl(false);
                    }
                    this.filesForm = new FormGroup(fileItems);
                    this.uploadForm = new FormGroup(items);
                    this.workOrder = order;
                    this.completeDialog.message = `${this.completeText} ${this.applicationNumber}`;
                    this.setSubheader();
                }                
        }),
            mergeMap(() => {
                return this.documentService.getAllDocumentTypes();
            })).subscribe((types: OnBaseDocumentType[]) => {
                this.documentTypes = types;
                this.documentTypes.unshift(this._defaultDocType);
                this.buildForm();
                this.workOrderLoading$.next(false);               
            },
            (error) => { console.error(error); });
        this.subscription.add(routeSubscription);           
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    buildForm(): void {
        const group: any = {};
        let paperUploads = this.workOrder.uploads.filter(u => this.isPaperSubmissionUpload(u));
        if (paperUploads && paperUploads.length > 0) {
            paperUploads.forEach(p => {
                let typeValue = this.getTypeValue(p); 
                group[p.key] = new FormControl({ value: typeValue, disabled: (typeValue == null ||  (this.workOrder.scanRequest != null && this.workOrder.scanRequest.documentType != null)) }, [Validators.required, Validators.min(0)])
            });
            this.uploadForm = this.builder.group(group);
        }        
    }

    public complete(): void {
        this.completeDialog.message = `${this.completeText} ${this.applicationNumber}`;
        this.completeDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.completeDialog);
    }

    deleteFile(file: IUploadFile): void {
        this.loader.toggleLoader(true);
        let deleteObs$ = this.updateContext().pipe().subscribe(
            (success) => {
                console.log(`context of updated following file delete`);  
                let value = (this.workOrder.scanRequest) ? this.workOrder.scanRequest.documentType : "";
                Object.keys(this.uploadForm.controls).forEach(key => {
                    this.uploadForm.get(key).patchValue(value);
                });            
                this.loader.toggleLoader(false);
            },
            (error) => { console.error(error); }
        );
        this.subscription.add(deleteObs$);
    }

    documentTypeSelected(question: FileUploadQuestion): void {
        let ctrl = this.uploadForm.get(question.key);
        if (ctrl && ctrl.valid) {
            let docType = this.documentTypes.find(t => t.id === parseInt(ctrl.value.toString()));
            if (docType != null) {
                this.loader.toggleLoader(true);
                let fileUpdate$ = this.updateFilesWithType(question, docType.name).pipe()
                    .subscribe((save) => {
                        console.log("context updated following type update");
                        this.loader.toggleLoader(false);
                    },
                        (error) => {
                            console.error(error);
                            this.loader.toggleLoader(false);
                        });
                this.subscription.add(fileUpdate$);
            }            
        }
    }

    downloadCoverSheet(): void {
        this.loader.toggleLoader(true);
        if (this.workOrder.scanRequest.coversheetUrl) {
            let subs$ = this.documentService.downloadFile(this.workOrder.scanRequest.coversheetUrl).pipe()
                .subscribe((data: string) => {
                    const link = this.download.nativeElement;
                    link.href = data;
                    link.click();
                    this.loader.toggleLoader(false);
                }, (error) => {
                    console.error(error);
                    this.loader.toggleLoader(false);
                    this.genericErrorDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.genericErrorDialog);
                });
            this.subscription.add(subs$);
        }
        else {
            let subs$ = this.documentService.getStoredDocument(this.workOrder.scanRequest.coversheetType, this.workOrder.scanRequest.coversheetId).pipe()
                .subscribe(
                    (data: any) => {
                        const blob = new Blob([data], { type: "application/pdf" });
                        const fileName = "coversheet.pdf";
                        let url = window.URL.createObjectURL(blob);
                        const link = this.download.nativeElement;
                        link.href = url;
                        link.download = fileName;
                        link.click();
                        this.loader.toggleLoader(false);
                    },
                    (error) => {
                        console.error(error);
                        this.loader.toggleLoader(false);
                        this.genericErrorDialog.toggleDialog(true);
                        this.dialogService.updateDialog(this.genericErrorDialog);
                    });
            this.subscription.add(subs$);
        }
    }

    getTypeValue(question: FileUploadQuestion): number {
        let typeVal: string = "-1";
        if (this.workOrder.scanRequest != null) {
            var documentType = this.documentTypes.find(t => t.name === this.workOrder.scanRequest.documentType);
            if (documentType != null) {
                typeVal = documentType.id.toString();
            }
        } else {
            let locationExt = "files";
            let files = jp.value(this.workOrder.flowInstance.context, `${question.contextTarget}.${locationExt}`);
            if (files instanceof Array) {
                let first = files.find(f => f.documentType != null);
                if (first) {
                    var documentType = this.documentTypes.find(t => t.name === first.documentType);
                    if (documentType != null) {
                        typeVal = documentType.id.toString();
                    }
                    
                }
            } else if (files != null) {
                if (files.documentType) {
                    var documentType = this.documentTypes.find(t => t.name === files.documentType);
                    if (documentType != null) {
                        typeVal = documentType.id.toString();
                    }                    
                }
            }
        }        
        return Number(typeVal);
    }

    getUploadInvalid(key: string): boolean {
        let invalid = false;
        let ctrl = this.uploadForm.get(key);
        if (ctrl) {
            invalid = ctrl.invalid;
        }
        return invalid;
    }

    hasUntypedFiles(question: FileUploadQuestion): boolean {
        if (this.workOrder.scanRequest && this.workOrder.scanRequest.documentType) return false;
        let hasUntyped: boolean = false;
        let locationExt = "files";
        let files = jp.value(this.workOrder.flowInstance.context, `${question.contextTarget}.${locationExt}`);
        if (files) {
            if (files instanceof Array) {
                hasUntyped = files.find(f => f.documentType == null || f.documentType === "") != null;
            } else {
                hasUntyped = files.documentType == null || files.documentType === "";
            }
        }
        return hasUntyped;
    }


    public exit(): void {
        this.router.navigate(["/home"]);
    }

    isPaperSubmissionUpload(upload: FileUploadQuestion): boolean {
        let config = upload.config;
        return config.onBehalf != null && config.onBehalf;
    }

    

    public onDialogConfirm(): void {
        this.loader.toggleLoader(true);
        this.workOrderService.changeStatus(this.workOrder.workOrderId, WorkOrderStatus.Completed).subscribe(data => {
            this.loader.toggleLoader(false);
            this.router.navigate(["/home"]);
        }, error => {
            this.loader.toggleLoader(false);
            console.log(`Failed to change status of work order ${this.workOrder.workOrderId} to Completed - ${error}`);
        });
    }

    public onDialogClose(): void {
        console.log("dialog cancelled");
    }

    public saveFile(file: any): void {
        let obs$: Observable<any>;
        if (this.workOrder.scanRequest != null && this.workOrder.scanRequest.documentType != null && this.paperQuestion != null) {            
            obs$ = this.updateFilesWithType(this.paperQuestion, this.workOrder.scanRequest.documentType);
        } else {
            obs$ = this.updateContext();
        }        
        this.loader.toggleLoader(true);
        let saveObs$ = obs$.pipe(
            mergeMap((data) => {
                let historyObj = {
                    instanceId: this.workOrder.flowInstance.id,
                    organizationId: this.workOrder.organizationId,
                    workOrderId: this.workOrder.workOrderId,
                    entryType: EntryType.DocumentScanned
                } as FlowHistory;
                this.loader.toggleLoader(true);
                return this.historyService.saveHistory(historyObj);
            })
        ).subscribe(
            (success) => {
                this.loader.toggleLoader(false);
                if (file.context) {
                    let question = this.workOrder.uploads.find(f => f.contextTarget === file.context);
                    if (question != null) {
                        let ctrl = this.uploadForm.get(question.key);
                        if (ctrl != null) {
                            ctrl.enable();
                        }
                    }
                }
            },
            (error) => {
                console.error(error);
                this.errorDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.errorDialog);
                this.loader.toggleLoader(false);
            }
        );
        this.subscription.add(saveObs$);
    }

    setSubheader(): void {
        let header: SubHeader = this.subheader;
        if (this.workOrder.scanRequest != null) {
            if (header.buttons == null) {
                header.buttons = [];
            }
            if (header.buttons.length === 0) {
                let button = { name: "Download Cover Sheet", method: () => this.downloadCoverSheet() };
                header.buttons.push(button);
            }
        }
        this.subheaderService.updateSubHeader(header);
    }

    updateContext(): Observable<any> {
        console.log("in upload scanned documents update context");
        return from(this.workOrder.flowInstance.saveContext()).pipe(tap((data) => {
            console.log(`context updated`);
            this.loader.toggleLoader(false);
        }));
    }

    updateFilesWithType(question: FileUploadQuestion, docType: string): Observable<any> {
        let locationExt = "files";
        let value = jp.value(this.workOrder.flowInstance.context, `${question.contextTarget}.${locationExt}`);
        if (value instanceof Array) {
            let arrValue = value as Array<any>;
            arrValue.forEach(a => a.documentType = docType);
        } else if (value.documentType != null) {
            value.documentType = docType;
        }
        jp.value(this.workOrder.flowInstance.context, `${question.contextTarget}.${locationExt}`, value);
        return this.updateContext();
    }
}
